import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';

import {
  Button,
  Spacer,
  TabPanel,
  Table,
  Flex,
  Text,
  IconNames,
  TableContainer,
  spaces,
} from '@electricjs/arc';

import { AssetDeviceProperty } from '@/types/assets';
import { DeviceIssueType } from '@/types/devices';
import { Employee } from '@/types/employees';
import EmailEmployeeButton from '@/components/Assets/AssetDetails/DeviceSecurityPanel/EmailEmployeeButton';
import { IssueCheckDisabledBanner } from '@/components/Assets/AssetDetails/IssueCheckDisabledBanner';
import { AssetDetailsTabs } from '@/pages/Assets/types';
import getSecurityPoliciesColumns from '@/components/Assets/AssetDetails/DeviceSecurityPanel/getSecurityPoliciesColumns';
import getPerformanceIssuesColumns from '@/components/Assets/AssetDetails/DeviceSecurityPanel/getPerformanceIssuesColumns';

export const getDeviceIssueNameMap = (t: TFunction) => ({
  [DeviceIssueType.Firewall]: t('deviceSecurityPanel.deviceIssues.firewall'),
  [DeviceIssueType.Gatekeeper]: t(
    'deviceSecurityPanel.deviceIssues.gatekeeper'
  ),
  [DeviceIssueType.OS]: t('deviceSecurityPanel.deviceIssues.osUpdate'),
  [DeviceIssueType.Encryption]: t(
    'deviceSecurityPanel.deviceIssues.diskEncryption'
  ),
  [DeviceIssueType.RAM]: t('deviceSecurityPanel.deviceIssues.ram'),
  [DeviceIssueType.Storage]: t('deviceSecurityPanel.deviceIssues.diskSpace'),
});

// Keep old export for backward compatibility during migration
export const DeviceIssueNameMap = {
  [DeviceIssueType.Firewall]: 'Firewall',
  [DeviceIssueType.Gatekeeper]: 'Gatekeeper',
  [DeviceIssueType.OS]: 'OS update',
  [DeviceIssueType.Encryption]: 'Disk encryption',
  [DeviceIssueType.RAM]: 'RAM',
  [DeviceIssueType.Storage]: 'Disk space',
};

export const getDeviceIssueStatus = (t: TFunction) => ({
  Enabled: t('deviceSecurityPanel.deviceStatus.enabled'),
  Issue: t('deviceSecurityPanel.deviceStatus.issueDetected'),
  Failed: t('deviceSecurityPanel.deviceStatus.failed'),
  Unknown: t('deviceSecurityPanel.deviceStatus.unknown'),
  Good: t('deviceSecurityPanel.deviceStatus.good'),
});

// Keep old export for backward compatibility during migration
export const DeviceIssueStatus = {
  Enabled: 'Enabled',
  Issue: 'Issue detected',
  Failed: 'Failed',
  Unknown: 'Unknown',
  Good: 'Good',
} as const;

export type DeviceIssuesData = {
  name: string;
  icon: IconNames;
  // Indicates whether data is available in the device reporting.
  hasData: boolean;
  // Specifies if an issue has been detected.
  hasIssue: boolean;
  // Determines if the "Check issue" feature is disabled.
  disabled: boolean;
  // Status of the policy
  status: (typeof DeviceIssueStatus)[keyof typeof DeviceIssueStatus];
  value?: string;
  action?: () => React.ReactNode;
};

type DeviceSecurityPanelProps = {
  device: AssetDeviceProperty;
  employee: Employee | undefined;
  isLoading: boolean;
};

const getIssueStatus = (
  issueValue: boolean | undefined | null,
  hasIssue: boolean
) => {
  if (issueValue === undefined || issueValue === null) {
    return DeviceIssueStatus.Unknown;
  }
  if (hasIssue) {
    return DeviceIssueStatus.Issue;
  }
  return issueValue ? DeviceIssueStatus.Enabled : DeviceIssueStatus.Failed;
};

const getPerformanceIssueStatus = (hasData: boolean, hasIssue: boolean) => {
  if (!hasData) {
    return DeviceIssueStatus.Unknown;
  }
  return hasIssue ? DeviceIssueStatus.Issue : DeviceIssueStatus.Good;
};

const DeviceSecurityPanel = ({
  device,
  employee,
  isLoading,
}: DeviceSecurityPanelProps) => {
  const { t } = useTranslation('assets');
  const navigate = useNavigate();
  const { space_m, space_l } = spaces;
  const devicePerformanceIssuesColumns = getPerformanceIssuesColumns(t);
  const deviceSecurityPoliciesColumns = getSecurityPoliciesColumns(t);
  const deviceIssueNameMap = getDeviceIssueNameMap(t);
  const deviceIssues = useMemo<{
    [key in DeviceIssueType]?: { extra?: { disabled: boolean } };
  }>(() => {
    return device?.reportingData?.issues || {};
  }, [device]);

  const isDisabled = useCallback(
    (key: DeviceIssueType) => {
      const issue = deviceIssues[key];
      if (typeof issue === 'object' && issue !== null && 'extra' in issue) {
        return !!issue.extra?.disabled;
      }
      return false;
    },
    [deviceIssues]
  );

  const deviceIssuesData: DeviceIssuesData[] = useMemo(
    () => [
      {
        name: deviceIssueNameMap[DeviceIssueType.Firewall],
        icon: 'firewall' as IconNames,
        hasData: !!device.reportingData?.firewall,
        hasIssue: !!deviceIssues && DeviceIssueType.Firewall in deviceIssues,
        disabled: isDisabled(DeviceIssueType.Firewall),
        status: getIssueStatus(
          device.reportingData?.firewall,
          !!deviceIssues && DeviceIssueType.Firewall in deviceIssues
        ),
        action: () => <Text>{t('deviceSecurityPanel.rebootNeeded')}</Text>,
      },
      ...(device.osType === 'darwin'
        ? [
            {
              name: deviceIssueNameMap[DeviceIssueType.Gatekeeper],
              icon: 'logout' as IconNames,
              hasData: !!device.reportingData?.gatekeeper,
              hasIssue:
                !!deviceIssues && DeviceIssueType.Gatekeeper in deviceIssues,
              disabled: isDisabled(DeviceIssueType.Gatekeeper),
              status: getIssueStatus(
                device.reportingData?.gatekeeper,
                !!deviceIssues && DeviceIssueType.Gatekeeper in deviceIssues
              ),
              action: () => (
                <Text>{t('deviceSecurityPanel.rebootNeeded')}</Text>
              ),
            },
          ]
        : []),
      {
        name:
          device.osType === 'darwin'
            ? t('deviceSecurityPanel.encryptionFilevault')
            : t('deviceSecurityPanel.encryptionBitlocker'),
        icon: 'key' as IconNames,
        hasData: !!device.reportingData?.encryptionPercent,
        hasIssue: !!deviceIssues && DeviceIssueType.Encryption in deviceIssues,
        disabled: isDisabled(DeviceIssueType.Encryption),
        status: getIssueStatus(
          device.reportingData?.encryption,
          !!deviceIssues && DeviceIssueType.Encryption in deviceIssues
        ),
        action: () => <Text>{t('deviceSecurityPanel.rebootNeeded')}</Text>,
      },
      {
        name: t('deviceSecurityPanel.osUpdateSettings'),
        icon: 'cog' as IconNames,
        hasData: !!device.osType && !!device.reportingData?.osName,
        hasIssue: !!deviceIssues && DeviceIssueType.OS in deviceIssues,
        disabled: isDisabled(DeviceIssueType.OS),
        status: getIssueStatus(
          !!device.osType && !!device.reportingData?.osName ? true : null,
          !!deviceIssues && DeviceIssueType.OS in deviceIssues
        ),
        action: () => (
          <EmailEmployeeButton
            issueType={DeviceIssueType.OS}
            employee={employee}
            isLoading={isLoading}
            device={device}
          />
        ),
      },
    ],
    [
      device,
      deviceIssues,
      employee,
      isDisabled,
      isLoading,
      deviceIssueNameMap,
      t,
    ]
  );

  const performanceIssuesData: DeviceIssuesData[] = useMemo(() => {
    const { ramCapacity, storageCapacity, storageInUse } =
      device.reportingData || {};
    const hasRamIssue = !!deviceIssues && DeviceIssueType.RAM in deviceIssues;
    const hasStorageIssue =
      !!deviceIssues && DeviceIssueType.Storage in deviceIssues;

    return [
      {
        name: deviceIssueNameMap[DeviceIssueType.RAM],
        icon: 'ram' as IconNames,
        hasData: ramCapacity !== null && ramCapacity !== undefined,
        hasIssue: hasRamIssue,
        disabled: isDisabled(DeviceIssueType.RAM),
        status: getPerformanceIssueStatus(
          ramCapacity !== null && ramCapacity !== undefined,
          hasRamIssue
        ),
        value: `${ramCapacity} GB`,
        action: () => (
          <Button
            id="order-replacement-button"
            variant="outline"
            onClick={() => navigate('/get-hardware')}>
            {t('deviceSecurityPanel.orderReplacement')}
          </Button>
        ),
      },
      {
        name: deviceIssueNameMap[DeviceIssueType.Storage],
        icon: 'storage' as IconNames,
        hasData: !!storageCapacity,
        hasIssue: hasStorageIssue,
        disabled: isDisabled(DeviceIssueType.Storage),
        status: getPerformanceIssueStatus(
          !!storageInUse && !!storageCapacity,
          hasStorageIssue
        ),
        value:
          storageInUse && storageCapacity
            ? `${storageInUse}/${storageCapacity} GB`
            : t('deviceSecurityPanel.notApplicable'),
        action: () => (
          <Button
            id="order-replacement-button"
            variant="outline"
            onClick={() => navigate('/get-hardware')}>
            {t('deviceSecurityPanel.orderReplacement')}
          </Button>
        ),
      },
    ];
  }, [device, deviceIssues, isDisabled, navigate, deviceIssueNameMap, t]);

  return (
    <TabPanel tabId={AssetDetailsTabs.Health}>
      <IssueCheckDisabledBanner reportingData={device.reportingData} />
      <Spacer y={space_m} />
      <Flex gap={space_m} width="100%">
        <TableContainer flexDirection="column" flex="1" overflow="visible">
          <Text variant="heading-3" padding={space_l}>
            {t('deviceSecurityPanel.securityPolicies')}
          </Text>
          <Table
            id="device-security-policies-table"
            data={deviceIssuesData}
            columns={deviceSecurityPoliciesColumns}
            loading={false}
          />
        </TableContainer>
        <TableContainer flexDirection="column" flex="1" overflow="visible">
          <Text variant="heading-3" padding={space_l}>
            {t('deviceSecurityPanel.performanceIssues')}
          </Text>
          <Table
            id="device-performance-issues-table"
            data={performanceIssuesData}
            columns={devicePerformanceIssuesColumns}
            loading={false}
          />
        </TableContainer>
      </Flex>
    </TabPanel>
  );
};

export default DeviceSecurityPanel;
